import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { DateTime } from 'luxon';
import { ActivitySummaryState } from 'src/app/pages/activity-summary/store/state';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { AuthState } from 'src/app/store/auth/auth.state';
import { AgentGatewayContext } from './copilot.types';
import type { CopilotMentionSelection, CopilotPanelScope, CopilotAgentSettings } from './copilot.types';
import { normalizeInclusivePanelDateRange, resolvePanelTimezone } from './copilot-scope.util';
import {
  COPILOT_AGENT_SKILLS_FLAG,
  COPILOT_GATEWAY_BOOLEAN_FLAGS,
  COPILOT_MULTI_PERSONA_ENABLED,
  COPILOT_PHASE1_AGENT_SKILLS,
  resolveCopilotAgentSkills
} from './copilot.constants';

@Injectable({ providedIn: 'root' })
export class CopilotContextBuilder {
  constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly growthBook: GrowthBookService,
  ) {}

  /**
   * Page-aware context for the NestJS Agent Gateway. Derives the active report date range
   * and filters from the current URL query params (read-only; nothing is dispatched).
   */
  buildAgentGatewayContext(
    mentionSelection?: CopilotMentionSelection,
    panelScope?: CopilotPanelScope,
    agentSettings?: CopilotAgentSettings,
  ): AgentGatewayContext {
    const route = (this.router.url || '').split('?')[0];
    const company = this.store.selectSnapshot(AuthState.company);
    const user = this.store.selectSnapshot(AuthState.user);
    const { dateRange: routeDateRange, filters } = this.readRouteParams();

    const context: AgentGatewayContext = { route };
    if (company?.id) {
      context.companyId = company.id;
    }
    if (user?.id) {
      context.userId = user.id;
    }
    if (company?.role) {
      context.role = company.role;
    }
    if (company?.pricingPlan) {
      context.plan = company.pricingPlan;
    }

    const timezone = resolvePanelTimezone(
      mentionSelection,
      panelScope,
      this.store.selectSnapshot(AuthState.userTimezone),
      typeof company?.timezone === 'string' ? company.timezone : undefined,
      this.reportTimezoneForRoute(route),
    );
    if (timezone) {
      context.timezone = timezone;
    }

    const rawDateRange = panelScope?.dateRange ?? routeDateRange;
    const viewerTz = this.store.selectSnapshot(AuthState.userTimezone)?.trim() || undefined;
    const todayZone = viewerTz || timezone || 'utc';
    const todayDt = DateTime.now().setZone(todayZone);
    const today = (todayDt.isValid ? todayDt : DateTime.utc()).toISODate() ?? undefined;
    const dateRange = normalizeInclusivePanelDateRange(
      rawDateRange?.from,
      rawDateRange?.to,
      { timeZone: timezone, today },
    );
    if (dateRange) {
      context.dateRange = dateRange;
    } else if (rawDateRange?.from || rawDateRange?.to) {
      context.dateRange = rawDateRange;
    }

    const mergedFilters = this.mergeMentionFilters(filters, mentionSelection);
    if (mergedFilters) {
      context.filters = mergedFilters;
    }
    const agentSkills = resolveCopilotAgentSkills(
      this.growthBook.getFeatureValue(
        COPILOT_AGENT_SKILLS_FLAG,
        [...COPILOT_PHASE1_AGENT_SKILLS],
      ),
    );
    const gatewayFlags = Object.fromEntries(
      COPILOT_GATEWAY_BOOLEAN_FLAGS.map(key => [
        key,
        this.growthBook.getFeatureValue(key, false) === true,
      ]),
    );
    context.featureFlags = {
      ...gatewayFlags,
      ...(agentSettings?.decisionEngineEnabled ? { decisionEngine: true } : {}),
      ...(COPILOT_MULTI_PERSONA_ENABLED && agentSettings?.advisorPersona
        ? { advisorPersona: agentSettings.advisorPersona }
        : {}),
      ...(COPILOT_MULTI_PERSONA_ENABLED && agentSettings?.tempoPersonality
        ? { tempoPersonality: agentSettings.tempoPersonality }
        : {}),
      ...(agentSettings?.agentLongNarrativeEnabled
        ? { agentLongNarrative: true }
        : {}),
      agentSkills,
    };
    return context;
  }

  /** Merge @-picker selections into gateway filters (preferred over loose name guessing). */
  private mergeMentionFilters(
    routeFilters: Record<string, unknown> | undefined,
    mentionSelection?: CopilotMentionSelection,
  ): Record<string, unknown> | undefined {
    const out = { ...(routeFilters ?? {}) };
    const users = mentionSelection?.selectedUsers?.filter((u) => u.id) ?? [];
    const groups = mentionSelection?.selectedGroups?.filter((g) => g.id) ?? [];
    if (users.length) {
      out.selectedUsers = users;
    }
    if (groups.length) {
      out.selectedGroups = groups;
    }
    return Object.keys(out).length ? out : undefined;
  }

  /** Activity Summary stores its report TZ separately from the viewer profile. */
  private reportTimezoneForRoute(route: string): string | undefined {
    if (!route.includes('/activity-summary')) {
      return undefined;
    }
    const tz = this.store.selectSnapshot(ActivitySummaryState.timezone);
    return typeof tz === 'string' && tz.trim() ? tz.trim() : undefined;
  }

  /** Pull the active report date range (`from`/`to`) and remaining filters from the URL. */
  private readRouteParams(): {
    dateRange?: { from?: string, to?: string };
    filters?: Record<string, unknown>;
  } {
    const params = this.router.parseUrl(this.router.url).queryParams ?? {};
    const { from, to, ...rest } = params;

    const dateRange = from || to ? { ...(from ? { from } : {}), ...(to ? { to } : {}) } : undefined;
    const filters = this.normalizeUserFilters(rest);
    return {
      dateRange,
      filters: Object.keys(filters).length ? filters : undefined,
    };
  }

  /**
   * When Ask AI overrides the sticky period from the user's message, keep the
   * active report URL `from`/`to` in sync so the next panel open / page filter
   * does not snap back to the old window.
   */
  syncRouteDateRange(from: string, to: string): void {
    const start = (from ?? '').trim();
    const end = (to ?? '').trim();
    if (!start || !end) {
      return;
    }
    const tree = this.router.parseUrl(this.router.url);
    const currentFrom = typeof tree.queryParams.from === 'string' ? tree.queryParams.from : '';
    const currentTo = typeof tree.queryParams.to === 'string' ? tree.queryParams.to : '';
    if (currentFrom === start && currentTo === end) {
      return;
    }
    // Only touch routes that already carry report date query params (or empty
    // ones on known report screens) — avoid rewriting unrelated pages.
    const path = tree.root.children?.primary?.segments?.map((s) => s.path).join('/') ?? '';
    const hasDateParams = Boolean(currentFrom || currentTo);
    const isReportRoute =
      path.includes('activity-summary') ||
      path.includes('timeline') ||
      path.includes('web-and-app') ||
      path.includes('hours-tracked') ||
      path.includes('projects') ||
      path.includes('timesheet');
    if (!hasDateParams && !isReportRoute) {
      return;
    }
    void this.router.navigate([], {
      queryParams: { from: start, to: end },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Map common user/group/project query params so the gateway can skip
   * name resolution when the UI already has filters selected.
   */
  private normalizeUserFilters(
    params: Record<string, unknown>,
  ): Record<string, unknown> {
    const out = { ...params };
    const userIds: string[] = [];

    const rawIds = params.userIds;
    if (Array.isArray(rawIds)) {
      userIds.push(...rawIds.map(String).filter(Boolean));
    } else if (typeof rawIds === 'string' && rawIds.trim()) {
      userIds.push(...rawIds.split(',').map((s) => s.trim()).filter(Boolean));
    }

    for (const key of ['userId', 'user', 'memberId', 'employeeId']) {
      const val = params[key];
      if (typeof val === 'string' && val.trim()) {
        userIds.push(val.trim());
      }
    }

    if (userIds.length) {
      out.userIds = [...new Set(userIds)];
      delete out.user;
      delete out.userId;
      delete out.memberId;
      delete out.employeeId;
    }

    const tagIds = this.collectIdList(params, [
      'tagIds',
      'tagId',
      'groupIds',
      'groupId',
      'teamIds',
      'teamId',
    ]);
    if (tagIds.length) {
      out.tagIds = tagIds;
      delete out.tagId;
      delete out.groupIds;
      delete out.groupId;
      delete out.teamIds;
      delete out.teamId;
    }

    const projectIds = this.collectIdList(params, [
      'projectIds',
      'projectId',
      'project',
    ]);
    if (projectIds.length) {
      out.projectIds = projectIds;
      delete out.projectId;
      delete out.project;
    }

    return out;
  }

  private collectIdList(
    params: Record<string, unknown>,
    keys: string[],
  ): string[] {
    const ids: string[] = [];
    for (const key of keys) {
      const val = params[key];
      if (Array.isArray(val)) {
        ids.push(...val.map(String).filter(Boolean));
      } else if (typeof val === 'string' && val.trim()) {
        ids.push(...val.split(',').map((s) => s.trim()).filter(Boolean));
      }
    }
    return [...new Set(ids)];
  }
}
